import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { usePriceFilterScroll } from '../hooks/usePriceFilterScroll';
import {
  PRICE_FILTER_COMPACT_SIZE_CLASSES,
  PRICE_FILTER_REGULAR_SIZE_CLASSES,
} from '../consts/priceFilters';

interface PriceFilterItem {
  price: number;
  color: string;
  isActive: boolean;
}

interface PriceFiltersProps {
  items: PriceFilterItem[];
  activePrices: number[];
  isMobile: boolean;
  onToggle: (price: number) => void;
}

export function PriceFilters({ items, activePrices, isMobile, onToggle }: PriceFiltersProps) {
  const {
    wrapperRef,
    scrollRef,
    contentRef,
    regularMeasureRef,
    compactMeasureRef,
    isCompact,
    compactPaddingInline,
    compactFontSize,
    compactGap,
    hasOverflow,
    canScrollPrevious,
    canScrollNext,
    scrollByPage,
  } = usePriceFilterScroll(isMobile, items.length);
  const showArrows = !isMobile && hasOverflow;

  if (items.length === 0) return null;

  return (
    <div className="mb-2 w-full">
      <div ref={wrapperRef} className="relative flex min-w-0 items-center gap-1">
        {!isMobile && (
          <div
            aria-hidden="true"
            className="pointer-events-none invisible absolute inset-x-0 top-0 h-0 overflow-hidden"
          >
            <div ref={regularMeasureRef} className="flex w-max flex-nowrap gap-1">
              {items.map(item => (
                <span
                  key={item.price}
                  className={cn(
                    'shrink-0 whitespace-nowrap border',
                    PRICE_FILTER_REGULAR_SIZE_CLASSES
                  )}
                >
                  {item.price} ₽
                </span>
              ))}
            </div>
            <div ref={compactMeasureRef} className="flex w-max flex-nowrap gap-1 text-xs">
              {items.map(item => (
                <span
                  key={item.price}
                  className={cn(
                    'shrink-0 whitespace-nowrap border',
                    PRICE_FILTER_COMPACT_SIZE_CLASSES
                  )}
                  style={{ fontSize: 'inherit' }}
                >
                  {item.price} ₽
                </span>
              ))}
            </div>
          </div>
        )}
        {showArrows && (
          <button
            type="button"
            aria-label="Показать предыдущие цены"
            disabled={!canScrollPrevious}
            onClick={() => scrollByPage(-1)}
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-surface-elevated text-text-secondary hover:bg-surface-card disabled:cursor-default disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        <div
          ref={scrollRef}
          className={cn(
            'min-w-0 flex-1 overflow-x-auto scrollbar-hide',
            isMobile ? 'px-1 py-1 [scroll-snap-type:x_proximity]' : 'py-1'
          )}
          style={{ overscrollBehaviorX: 'contain', touchAction: 'pan-x' }}
        >
          <div
            ref={contentRef}
            className={cn('flex w-max flex-nowrap', isMobile ? 'gap-2' : 'gap-1')}
            style={{ gap: !isMobile && isCompact ? compactGap : undefined }}
          >
            {items.map(item => (
              <button
                key={`price-${item.price}`}
                type="button"
                aria-pressed={item.isActive}
                onClick={() => onToggle(item.price)}
                className={cn(
                  'shrink-0 cursor-pointer whitespace-nowrap rounded-full border transition-opacity',
                  isMobile
                    ? 'px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm [scroll-snap-align:start]'
                    : isCompact
                      ? PRICE_FILTER_COMPACT_SIZE_CLASSES
                      : PRICE_FILTER_REGULAR_SIZE_CLASSES,
                  activePrices.length === 0 || item.isActive
                    ? 'opacity-100'
                    : 'opacity-50 hover:opacity-80'
                )}
                style={{
                  borderColor: item.color,
                  backgroundColor: item.isActive ? item.color : 'var(--surface-card)',
                  color: item.isActive ? 'var(--text-inverse)' : item.color,
                  paddingInline: !isMobile && isCompact ? compactPaddingInline : undefined,
                  fontSize: !isMobile && isCompact ? compactFontSize : undefined,
                }}
              >
                {item.price} ₽
              </button>
            ))}
          </div>
        </div>
        {showArrows && (
          <button
            type="button"
            aria-label="Показать следующие цены"
            disabled={!canScrollNext}
            onClick={() => scrollByPage(1)}
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-surface-elevated text-text-secondary hover:bg-surface-card disabled:cursor-default disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
